import api from './api';

// ---------------------------------------------------------------------------
// Types — mirror of figano-backend adminLedger.service normalized row
// ---------------------------------------------------------------------------

export type LedgerSource =
  | 'user_wallet'
  | 'seller_balance'
  | 'seller_withdrawal'
  | 'gateway'
  | 'banner'
  | 'inspection_penalty';

export type LedgerDirection = 'credit' | 'debit' | 'neutral';

export interface LedgerRow {
  source: LedgerSource;
  type: string;
  direction: LedgerDirection;
  amount: number;
  status: string;
  date: string;
  description: string;
  userId?: string | null;
  storeId?: string | null;
  orderId?: string | null;
  refId?: string | null;
  orderNumber?: string | null;
  docId?: string;
  txId?: string | null;
  user?: { _id: string; firstName?: string; lastName?: string; phoneNumber?: string; email?: string } | null;
  store?: { _id: string; name?: string } | null;
  /** درگاه/کانال پول این ردیف (کانال خود ردیف، وگرنه درگاه سفارش مرتبط) */
  gateway?: string | null;
  /** درگاه سفارش مرتبط (paymentDetails.provider) */
  orderGateway?: string | null;
  /** سفارش مرتبط پرداخت ترکیبی بوده */
  orderSplit?: boolean;
  /** مرحلهٔ پرداخت ترکیبی روی ردیف درگاه: cash | remainder | bnpl */
  splitLeg?: string | null;
  /** کارمزد اقساطی روی ردیف verify درگاه */
  surchargeAmount?: number | null;
}

export interface LedgerFilterParams {
  from?: string;
  to?: string;
  sources?: LedgerSource[];
  types?: string[];
  gateways?: string[];
  direction?: LedgerDirection;
  statuses?: string[];
  minAmount?: number;
  maxAmount?: number;
  search?: string;
  userQuery?: string;
  userId?: string;
  storeQuery?: string;
  storeId?: string;
  page?: number;
  limit?: number;
  sortBy?: 'date' | 'amount';
  sortDir?: 'asc' | 'desc';
}

export interface LedgerSummary {
  credit: number;
  debit: number;
  net: number;
  count: number;
  bySource: Array<{ source: LedgerSource; credit: number; debit: number; count: number }>;
}

export interface StatementRow {
  id: string;
  date: string;
  type: string;
  direction: LedgerDirection;
  amount: number;
  status: string;
  description: string;
  refId?: string | null;
  orderNumber?: string | null;
  orderId?: string | null;
  balanceAfter: number;
  gateway?: string | null;
  orderGateway?: string | null;
  orderSplit?: boolean;
}

export interface UserStatement {
  user: { id: string; name: string; phoneNumber?: string; email?: string } | null;
  snapshot: {
    balance: number; lockedBalance: number; blockedForWithdrawal: number;
    totalEarned: number; totalSpent: number; totalWithdrawn: number;
  } | null;
  openingBalance: number;
  closingBalance: number;
  totals: { credit: number; debit: number; count: number };
  rows: StatementRow[];
  total: number;
  page: number;
  limit: number;
}

export interface StoreStatement {
  store: { id: string; name: string } | null;
  snapshot: {
    totalEarnings: number; pendingBalance: number;
    availableBalance: number; totalWithdrawn: number;
    blockedAmount?: number; debt?: number; refundObligationsTotal?: number;
    /** true = از محاسبهٔ لحظه‌ای (calculateRealBalance)، نه فیلدهای ذخیره‌شده */
    live?: boolean;
  } | null;
  openingBalance: number;
  closingBalance: number;
  totals: { credit: number; debit: number; count: number };
  rows: StatementRow[];
  withdrawals: Array<{
    id: string; amount: number; netAmount?: number; processingFee?: number;
    status: string; requestedAt: string; processedAt?: string;
    bankName?: string; referenceId?: string; payoutMode?: string; payoutStatus?: string;
  }>;
  total: number;
  page: number;
  limit: number;
}

export interface PeriodicReport {
  period: 'daily' | 'monthly';
  range: { from: string; to: string };
  rows: Array<{
    period: string;
    firstDay: string;
    credit: number;
    debit: number;
    net: number;
    count: number;
    /** ورود واقعی پول (پرداخت‌های موفق درگاه) — بدون ثبت‌های موازی دفاتر */
    moneyIn: number;
    /** خروج واقعی پول (استرداد درگاه + برداشت‌های تکمیل‌شده) */
    moneyOut: number;
    moneyNet: number;
    bySource: Record<string, { credit: number; debit: number }>;
  }>;
  totals: {
    credit: number; debit: number; net: number; count: number;
    moneyIn: number; moneyOut: number; moneyNet: number;
  };
}

// ---------------------------------------------------------------------------
// Persian labels (kept in sync with backend adminLedger.service)
// ---------------------------------------------------------------------------

export const SOURCE_LABELS: Record<LedgerSource, string> = {
  user_wallet: 'کیف‌پول کاربر',
  seller_balance: 'موجودی فروشنده',
  seller_withdrawal: 'برداشت فروشنده',
  gateway: 'درگاه پرداخت',
  banner: 'بنر تبلیغاتی',
  inspection_penalty: 'جریمه بازرسی',
};

/** برچسب و رنگ درگاه/کانال پول — ستون «درگاه» */
export const GATEWAY_LABELS: Record<string, string> = {
  zarinpal: 'زرین‌پال', snapppay: 'اسنپ‌پی', digipay: 'دیجی‌پی', payping: 'پی‌پینگ', wallet: 'کیف پول', mock: 'آزمایشی',
  zarinpal_payout: 'واریز خودکار زرین‌پال', bank_manual: 'واریز دستی بانکی',
};
export const GATEWAY_COLORS: Record<string, string> = {
  zarinpal: '#b8860b', snapppay: '#008EFA', digipay: '#0b3d91', payping: '#2e7d32', wallet: '#6e6e73', mock: '#9e9e9e',
  zarinpal_payout: '#b8860b', bank_manual: '#546e7a',
};
export const GATEWAY_OPTIONS: Array<[string, string]> = [
  ['zarinpal', 'زرین‌پال'], ['snapppay', 'اسنپ‌پی'], ['digipay', 'دیجی‌پی'], ['payping', 'پی‌پینگ'], ['wallet', 'کیف پول'],
  ['zarinpal_payout', 'واریز خودکار زرین‌پال'], ['bank_manual', 'واریز دستی بانکی'],
];
export const SPLIT_LEG_LABELS: Record<string, string> = { cash: 'مرحلهٔ ۱ نقدی', remainder: 'باقی‌ماندهٔ نقدی', bnpl: 'مرحلهٔ ۲ اقساطی' };

export const TYPE_LABELS: Record<string, string> = {
  refund: 'بازگشت وجه', admin_bonus: 'جایزه ادمین', admin_deduction: 'کسر ادمین',
  purchase_payment: 'پرداخت خرید از کیف‌پول', withdrawal: 'برداشت', deposit: 'شارژ کیف‌پول',
  withdrawal_reversal: 'برگشت برداشت ناموفق',
  referral_bonus: 'پاداش ریفرال', postal_refund_lock: 'قفل بازپرداخت پستی',
  postal_refund_release: 'آزادسازی بازپرداخت پستی', postal_refund_reversal: 'برگشت بازپرداخت پستی',
  sale: 'فروش', commission: 'کارمزد پلتفرم', adjustment: 'اصلاح حساب',
  cancellation: 'لغو سفارش', inspection_fee: 'هزینه بازرسی', penalty: 'جریمه',
  refund_reversal: 'برگشت بازپرداخت',
  seller_withdrawal_request: 'درخواست برداشت فروشنده',
  gateway_payment: 'پرداخت درگاه', gateway_refund: 'استرداد درگاه',
  gateway_payment_split: 'پرداخت درگاه (پرداخت ترکیبی)', gateway_refund_split: 'استرداد درگاه (پرداخت ترکیبی)',
  withdrawal_fee_income: 'کارمزد برداشت (درآمد پلتفرم)',
  referral_bonus_cost: 'پاداش ریفرال (هزینه پلتفرم)',
  banner_payment: 'پرداخت رزرو بنر', banner_refund: 'استرداد رزرو بنر',
  penalty_late_inspection: 'جریمه تأخیر بازرسی',
  penalty_late_shipment: 'جریمه تأخیر ارسال',
  penalty_dispute_penalty: 'جریمه شکایت',
};

export const STATUS_LABELS: Record<string, string> = {
  completed: 'تکمیل‌شده', pending: 'در انتظار', failed: 'ناموفق', cancelled: 'لغوشده',
  success: 'موفق', approved: 'تأییدشده', processing: 'در حال پردازش',
  rejected: 'ردشده', waived: 'بخشوده‌شده', paid: 'پرداخت‌شده', refunded: 'استردادشده',
};

export const DIRECTION_LABELS: Record<LedgerDirection, string> = {
  credit: 'بستانکار',
  debit: 'بدهکار',
  neutral: 'خنثی',
};

/**
 * جهت هر سند نسبت به «حساب صاحب سند» است، نه پلتفرم.
 * مثال رفاند = دو سند: بدهکارِ «حساب فروشنده» + بستانکارِ «کیف‌پول خریدار».
 */
export const OWNER_LABELS: Record<LedgerSource, string> = {
  user_wallet: 'کیف‌پول کاربر',
  seller_balance: 'حساب فروشنده',
  seller_withdrawal: 'حساب فروشنده',
  gateway: 'پلتفرم',
  banner: 'پلتفرم',
  inspection_penalty: 'حساب بازرس',
};

/** Types grouped by source — drives the dependent "نوع تراکنش" filter. */
export const TYPES_BY_SOURCE: Record<LedgerSource, string[]> = {
  user_wallet: [
    'deposit', 'purchase_payment', 'refund', 'withdrawal', 'withdrawal_reversal', 'admin_bonus', 'admin_deduction',
    'referral_bonus', 'postal_refund_lock',
    'postal_refund_release', 'postal_refund_reversal',
  ],
  seller_balance: ['sale', 'commission', 'withdrawal', 'refund', 'adjustment', 'cancellation', 'inspection_fee', 'penalty', 'refund_reversal'],
  seller_withdrawal: ['seller_withdrawal_request'],
  gateway: ['gateway_payment', 'gateway_payment_split', 'gateway_refund', 'gateway_refund_split', 'withdrawal_fee_income', 'referral_bonus_cost'],
  banner: ['banner_payment', 'banner_refund'],
  inspection_penalty: ['penalty_late_inspection', 'penalty_late_shipment', 'penalty_dispute_penalty'],
};

// ---------------------------------------------------------------------------
// Query-string builder + service
// ---------------------------------------------------------------------------

function buildQuery(params: Record<string, any>): string {
  const parts: string[] = [];
  Object.entries(params).forEach(([key, value]) => {
    if (value == null || value === '' || (Array.isArray(value) && value.length === 0)) return;
    const v = Array.isArray(value) ? value.join(',') : String(value);
    parts.push(`${key}=${encodeURIComponent(v)}`);
  });
  return parts.length > 0 ? `?${parts.join('&')}` : '';
}

const financeLedgerService = {
  getLedger: async (params: LedgerFilterParams) => {
    return api.get<{ rows: LedgerRow[]; total: number; page: number; limit: number }>(
      `/admin/finance/ledger${buildQuery(params as any)}`
    );
  },

  // `opts.silentForbidden` lets dashboard widgets suppress the global 403 redirect
  // so a missing ledger permission only hides that widget.
  getLedgerSummary: async (params: LedgerFilterParams, opts?: { silentForbidden?: boolean }) => {
    return api.get<LedgerSummary>(
      `/admin/finance/ledger/summary${buildQuery(params as any)}`,
      opts?.silentForbidden ? ({ silentForbidden: true } as any) : undefined,
    );
  },

  /** Downloads the filtered ledger as an Excel-friendly CSV. */
  exportLedger: async (params: LedgerFilterParams) => {
    const blob = await api.get<Blob>(
      `/admin/finance/ledger/export${buildQuery(params as any)}`,
      { responseType: 'blob' } as any
    );
    const url = window.URL.createObjectURL(blob instanceof Blob ? blob : new Blob([blob as any]));
    const a = document.createElement('a');
    a.href = url;
    a.download = `ledger-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },

  searchParties: async (kind: 'user' | 'store', q: string) => {
    return api.get<Array<{ id: string; name: string; phoneNumber?: string; email?: string }>>(
      `/admin/finance/statement/parties${buildQuery({ kind, q })}`
    );
  },

  getUserStatement: async (userId: string, params: { from?: string; to?: string; page?: number; limit?: number }) => {
    return api.get<UserStatement>(`/admin/finance/statement/user/${userId}${buildQuery(params)}`);
  },

  getStoreStatement: async (storeId: string, params: { from?: string; to?: string; page?: number; limit?: number }) => {
    return api.get<StoreStatement>(`/admin/finance/statement/store/${storeId}${buildQuery(params)}`);
  },

  /** 🧾 ریز موجودی فروشگاه با دلیل هر آیتم + فرمول */
  getStoreExplain: async (storeId: string) => {
    return api.get<StoreBalanceExplain>(`/admin/finance/statement/store/${storeId}/explain`);
  },

  /** 🧾 دفتر رویدادهای مالی فروشنده */
  getStoreFundEvents: async (storeId: string, params: { page?: number; limit?: number; type?: string; search?: string; from?: string; to?: string }) => {
    return api.get<StoreFundEvents>(`/admin/finance/statement/store/${storeId}/fund-events${buildQuery(params as any)}`);
  },

  getPeriodicReport: async (params: { from?: string; to?: string; period?: 'daily' | 'monthly'; sources?: LedgerSource[] }) => {
    return api.get<PeriodicReport>(`/admin/finance/reports/periodic${buildQuery(params as any)}`);
  },
};

export interface StoreBalanceExplain {
  balance: Record<string, any> & { availableBalance: number; pendingBalance: number; blockedAmount: number; debt: number; refundObligationsTotal: number; refundShortfall: number; fundingPool: number; awaitingDeliveryAmount: number };
  breakdown: {
    items: { orderItemId: string; orderId: string; orderNumber: string; productTitle: string; quantity: number; itemStatus: string; amount: number; grossPrice: number; bucket: string; reasonCode?: string; reasonText?: string; deliveredAt?: string | null; releaseDate?: string | null; daysUntilRelease?: number | null; disputeStatus?: string | null; refundedAmount: number; obligation?: { amount: number; status: string } | null }[];
    withdrawals: { id: string; referenceId?: string; amount: number; processingFee: number; status: string; requestedAt?: string; processedAt?: string; counted: boolean; reasonText: string; payoutMode?: string; payoutStatus?: string }[];
    penalties: { amount: number; description?: string; date?: string }[];
    obligations: { id: string; amount: number; orderNumber?: string; status: string; createdAt?: string; reason?: string }[];
    formula: { key: string; label: string; amount: number; op: '+' | '-' | '=' | 'clamp' }[];
    settings: { digitalWarrantyDays: number; postalWarrantyDays: number; physicalWarrantyDays: number };
    generatedAt: string;
  };
}
export interface StoreFundEvent {
  _id: string; type: string; effect: 'credit' | 'debit' | 'lock' | 'unlock' | 'info'; amount: number; title: string; description?: string;
  bucketFrom?: string; bucketTo?: string; orderNumber?: string; orderItemId?: string; productTitle?: string; withdrawalReferenceId?: string;
  actorType: string; actorId?: { name?: string } | string; actorName?: string; createdAt: string;
}
export interface StoreFundEvents {
  rows: StoreFundEvent[];
  pagination: { page: number; limit: number; total: number; pages: number };
  labels: { types: Record<string, string>; buckets: Record<string, string> };
}

export default financeLedgerService;
